/**
 * Stale-chunk recovery.
 *
 * After a republish, a browser tab can still be holding the previous HTML shell,
 * whose <script> URLs no longer exist on the CDN. Every dynamic import then fails
 * with a "Failed to fetch dynamically imported module" / chunk-load error and the
 * app falls into the router error boundary ("This page didn't load").
 *
 * The fix is a cache-busting hard reload: re-request the document with a fresh
 * query param so the CDN/browser cache can't serve the old shell. We only do that
 * once per session to avoid an infinite reload loop when the failure is real.
 */

const RELOAD_FLAG = "mfi:stale-chunk-reloaded";
const CACHE_BUST_PARAM = "__v";

const STALE_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "unable to preload css",
  "loading chunk",
  "loading css chunk",
  "chunkloaderror",
  "'text/html' is not a valid javascript mime type",
];

export function isStaleChunkError(error: unknown): boolean {
  const message = `${(error as Error | undefined)?.name ?? ""} ${
    (error as Error | undefined)?.message ?? String(error ?? "")
  }`.toLowerCase();
  return STALE_PATTERNS.some((pattern) => message.includes(pattern));
}

export function hasAlreadyHardReloaded(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) === "1";
  } catch {
    return false;
  }
}

export function clearHardReloadFlag(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/** Reload the current URL with a cache-busting param so a fresh shell is fetched. */
export function hardReloadWithCacheBust(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    /* storage unavailable — still attempt the reload */
  }
  const url = new URL(window.location.href);
  url.searchParams.set(CACHE_BUST_PARAM, Date.now().toString(36));
  window.location.replace(url.toString());
}

/**
 * Auto-recover once from a stale shell. Returns true when a reload was triggered.
 */
export function recoverFromStaleChunk(error: unknown): boolean {
  if (typeof window === "undefined") return false;
  if (!isStaleChunkError(error)) return false;
  if (hasAlreadyHardReloaded()) return false;
  hardReloadWithCacheBust();
  return true;
}

/**
 * Global listeners for chunk failures that happen outside a React render
 * (e.g. a route's lazy import rejecting during navigation).
 */
export function installStaleChunkRecovery(): () => void {
  if (typeof window === "undefined") return () => {};

  // A successful load means the shell is current again.
  if (!document.querySelector("script[data-stale-shell]")) {
    // Defer so we don't clear before a failing import has a chance to fire.
    window.setTimeout(() => {
      if (!hasAlreadyHardReloaded()) return;
      clearHardReloadFlag();
    }, 10_000);
  }

  const onError = (event: ErrorEvent) => {
    recoverFromStaleChunk(event.error ?? event.message);
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    recoverFromStaleChunk(event.reason);
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
